/**
 * Shared utility for tier-based visibility configuration.
 * Used by both FriendPortfolioPage and PortfolioPage (View Portfolio As... feature).
 */

export type PortfolioViewTier = 'basic' | 'tier3' | 'tier2' | 'tier1' | 'full';

export interface TierVisibilityConfig {
  showPortfolioSummary: boolean;
  showTopMovers: boolean;
  showChart: boolean;
  showInvestmentTable: boolean;
  showTimePicker: boolean;
  hideToggle: boolean; // Hide %/$ toggle
  hideShares: boolean; // Hide "Shares" column in Investment Table
  hideMyTotal: boolean; // Hide "My Total" column in Investment Table
  showMyTotalText: boolean; // Show "My Total" text in header (hide text but keep column for Silver tier)
  enableAccountExpansion: boolean; // Enable account expansion arrows (hide for tiers without ticker visibility)
  hideShowRealizedGains: boolean; // Hide "Show realized gains" checkbox
  hideTradingActivityIcons: boolean; // Hide trading activity icons on chart
  tradingActivityFriendView: boolean; // Use 2-column layout in Trading Activity Modal
  portfolioSummaryFullWidth: boolean; // Portfolio Summary spans full width
  chartAndTableLayout: 'fullWidth' | 'sideBySide' | 'normal'; // Layout for Chart and Investment Table
}

/**
 * Get visibility configuration based on tier.
 * 
 * @param tier - The tier level ('basic', 'tier3', 'tier2', 'tier1', or 'full')
 * @returns Configuration object with visibility flags
 */
export function getTierVisibilityConfig(tier: PortfolioViewTier): TierVisibilityConfig {
  switch (tier) {
    case 'basic':
      return {
        showPortfolioSummary: false,
        showTopMovers: false,
        showChart: false,
        showInvestmentTable: false,
        showTimePicker: false,
        hideToggle: true,
        hideShares: true,
        hideMyTotal: true,
        showMyTotalText: false,
        enableAccountExpansion: false, // No ticker data visible
        hideShowRealizedGains: true,
        hideTradingActivityIcons: true,
        tradingActivityFriendView: true,
        portfolioSummaryFullWidth: false,
        chartAndTableLayout: 'normal',
      };

    case 'tier3':
      return {
        showPortfolioSummary: true,
        showTopMovers: false,
        showChart: true,
        showInvestmentTable: false,
        showTimePicker: true,
        hideToggle: true,
        hideShares: true,
        hideMyTotal: true,
        showMyTotalText: false,
        enableAccountExpansion: false, // No ticker data visible
        hideShowRealizedGains: true,
        hideTradingActivityIcons: true,
        tradingActivityFriendView: true,
        portfolioSummaryFullWidth: true, // Full width for Tier 3
        chartAndTableLayout: 'fullWidth', // Chart spans full width
      };

    case 'tier2':
      return {
        showPortfolioSummary: true,
        showTopMovers: false,
        showChart: true,
        showInvestmentTable: true,
        showTimePicker: true,
        hideToggle: true,
        hideShares: true,
        hideMyTotal: true, // Hide "My Total" column for Tier 2 (no account-level values)
        showMyTotalText: false, // Hide "My Total" text for Tier 2 (keep column structure)
        enableAccountExpansion: false, // No ticker data visible
        hideShowRealizedGains: true, // Hide for Tier 2
        hideTradingActivityIcons: true,
        tradingActivityFriendView: true,
        portfolioSummaryFullWidth: true, // Full width for Tier 2
        chartAndTableLayout: 'sideBySide', // Chart and Investment Table side by side (uses 3-column grid like main portfolio)
      };

    case 'tier1':
      return {
        showPortfolioSummary: true,
        showTopMovers: true,
        showChart: true,
        showInvestmentTable: true,
        showTimePicker: true,
        hideToggle: true,
        hideShares: true,
        hideMyTotal: false, // Show "My Total" for Tier 1
        showMyTotalText: true, // Show "My Total" text for Tier 1
        enableAccountExpansion: true, // Can see ticker data
        hideShowRealizedGains: false, // Can show for Tier 1
        hideTradingActivityIcons: false, // Show trading activity icons
        tradingActivityFriendView: true, // Use 2-column layout in modal (no description)
        portfolioSummaryFullWidth: false,
        chartAndTableLayout: 'sideBySide',
      };

    case 'full':
    default:
      return {
        showPortfolioSummary: true,
        showTopMovers: true,
        showChart: true,
        showInvestmentTable: true,
        showTimePicker: true,
        hideToggle: false, // Show toggle for full view
        hideShares: false, // Show shares column
        hideMyTotal: false, // Show "My Total" for full view
        showMyTotalText: true, // Show "My Total" text for full view
        enableAccountExpansion: true, // Can see ticker data
        hideShowRealizedGains: false, // Show checkbox
        hideTradingActivityIcons: false, // Show trading activity icons
        tradingActivityFriendView: false, // Full 3-column layout in modal
        portfolioSummaryFullWidth: false,
        chartAndTableLayout: 'normal',
      };
  }
}

/**
 * Convert friendship tier enum to PortfolioViewTier.
 * 
 * @param tier - Friendship tier enum ('TIER_1', 'TIER_2', 'TIER_3', 'BASIC')
 * @returns PortfolioViewTier
 */
export function friendshipTierToViewTier(tier: 'TIER_1' | 'TIER_2' | 'TIER_3' | 'BASIC'): PortfolioViewTier {
  switch (tier) {
    case 'TIER_1':
      return 'tier1';
    case 'TIER_2':
      return 'tier2';
    case 'TIER_3':
      return 'tier3';
    case 'BASIC':
      return 'basic';
    default:
      return 'basic';
  }
}

